import React, { useState } from 'react';
import SectionIcon from './SectionIcon';
import AddTagDialog from './AddTagDialog';
import { TARGET_AUDIENCE, MANAGEMENT_TAGS } from '../../../../services/api/fake_services/menu_management_fake_data';

const TagButton = ({ label, selected, onClick, onDelete, showDelete = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors flex items-center gap-2 text-left ${
      selected
        ? 'border-blue-btn-primary bg-blue-btn-primary/5 text-blue-btn-primary'
        : 'border-slate-200 text-slate-500'
    }`}
  >
    <span className="max-h-9 overflow-y-auto break-all default-scrollbar">{label}</span>
    {showDelete && (
      <span
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="ml-1 hover:text-red-500 cursor-pointer"
      >
        ×
      </span>
    )}
  </button>
);

export default function DetailedSettingsSection({
  values,
  setFieldValue,
  customTags,
  setCustomTags,
  readonly = false,
}) {
  const [showAddTagDialog, setShowAddTagDialog] = useState(false);

  const toggleTag = (tag) => {
    const current = values.managementTags;
    const updated = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    setFieldValue('managementTags', updated);
  };

  const addCustomTag = (tagName) => {
    if (tagName && !customTags.includes(tagName) && !MANAGEMENT_TAGS.includes(tagName)) {
      const newCustomTags = [...customTags, tagName];
      setCustomTags(newCustomTags);
      setFieldValue('managementTags', [...values.managementTags, tagName]);
    }
  };

  const deleteCustomTag = (tagToDelete) => {
    setCustomTags(customTags.filter((tag) => tag !== tagToDelete));
    setFieldValue(
      'managementTags',
      values.managementTags.filter((tag) => tag !== tagToDelete),
    );
  };

  const toggleTargetAudience = (target) => {
    const current = values.targetAudiences || [];
    const updated = current.includes(target) ? current.filter((t) => t !== target) : [...current, target];
    setFieldValue('targetAudiences', updated);
  };

  const selectedTags = MANAGEMENT_TAGS.filter((tag) => {
    return values.managementTags.includes(tag);
  });

  const selectedTargetAudiences = TARGET_AUDIENCE.filter((target) => {
    return values.targetAudiences.includes(target.value);
  });

  return (
    <div className="px-4 py-8 border-b border-slate-50">
      <SectionIcon title="詳細設定">
        <span className="material-symbols-outlined text-primary text-xl">tune</span>
      </SectionIcon>

      <div className="space-y-6">
        <div>
          {!readonly ? (
            <>
              <p className="text-sm font-bold mb-3">管理用タグ</p>
              <div className="flex flex-wrap gap-2">
                {MANAGEMENT_TAGS.map((tag) => (
                  <TagButton
                    key={tag}
                    label={tag}
                    selected={values.managementTags.includes(tag)}
                    onClick={() => toggleTag(tag)}
                    showDelete={false}
                  />
                ))}
                {customTags.map((tag) => (
                  <TagButton
                    key={tag}
                    label={tag}
                    selected={values.managementTags.includes(tag)}
                    onClick={() => toggleTag(tag)}
                    onDelete={() => deleteCustomTag(tag)}
                    showDelete={true}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setShowAddTagDialog(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-dashed border-slate-300 text-slate-400"
                >
                  <span className="material-symbols-outlined !text-lg">add</span>
                </button>
              </div>
            </>
          ) : selectedTags.length === 0 ? (
            <>
              <p className="text-sm font-bold mb-3">タグ</p>
              <p>なし</p>
            </>
          ) : (
            <>
              <p className="text-sm font-bold mb-3">タグ</p>
              <div className="flex flex-wrap gap-2">
                {MANAGEMENT_TAGS.filter((tag) => {
                  return values.managementTags.includes(tag);
                })
                  .concat(customTags)
                  .map((tag) => (
                    <TagButton key={tag} label={tag} selected={true} showDelete={false} />
                  ))}
              </div>
            </>
          )}
        </div>

        <AddTagDialog open={showAddTagDialog} onClose={() => setShowAddTagDialog(false)} onAdd={addCustomTag} />

        <div>
          <p className="text-sm font-bold mb-3">ターゲット層</p>
          <div className="grid grid-cols-3 gap-2">
            {!readonly ? (
              <>
                {TARGET_AUDIENCE.map((target) => (
                  <label
                    key={target.value}
                    className={`relative flex items-center justify-center p-3 rounded-xl cursor-pointer ${
                      values.targetAudiences.includes(target.value)
                        ? 'border border-blue-btn-primary bg-blue-btn-primary/5'
                        : 'border border-slate-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={values.targetAudiences.includes(target.value)}
                      onChange={() => toggleTargetAudience(target.value)}
                      className="hidden"
                    />
                    <span
                      className={`text-xs font-bold ${
                        values.targetAudiences.includes(target.value) ? 'text-blue-btn-primary' : 'text-slate-500'
                      }`}
                    >
                      {target.label}
                    </span>
                  </label>
                ))}
              </>
            ) : selectedTargetAudiences.length === 0 ? (
              <p>なし</p>
            ) : (
              <>
                {selectedTargetAudiences.map((target) => (
                  <label
                    key={target.value}
                    className={`relative flex items-center justify-center p-3 rounded-xl cursor-pointer border border-blue-btn-primary bg-blue-btn-primary/5`}
                  >
                    <input type="checkbox" checked={true} className="hidden" />
                    <span className={`text-xs font-bold text-blue-btn-primary`}>{target.label}</span>
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
